import { io, Socket } from 'socket.io-client'
import { useEditorStore } from '@/stores/editor.ts'
import { useChart } from '@/composables/chart/useChart.ts'
import { storeToRefs } from 'pinia'
import { nextTick } from 'vue'
import { axiosRequest } from '@/lib/axios/axios.ts'
import { useCommonStore } from '@/stores/common.ts'
import { addBlockList, createBlockEvent, deletePrefix, newBlockEvent, addPrefix } from '@/views/editor/composable/useNote.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'
import { calcFunction } from '@/views/editor/composable/table/useFunction'
import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { addFileBlock, allDeleteFile, deleteFile, downloadFile } from '@/views/editor/composable/useFile.ts'
import { recentlyPastedBlocks } from '@/views/editor/composable/usePaste.ts'
import {
	UseHistory,
	updateBaselineForRemoteModify,
	updateBaselineForRemoteCreate,
	updateBaselineForRemoteDelete,
	updateBaselineForRemoteCellUpdate,
	updateBaselineForRemoteBlockMove,
	updateBaselineBlockId,
	updateBaselineForRemoteTableBlock,
} from '@/views/editor/composable/useHistory.ts'
import { setTableData } from '@/views/editor/composable/useBlockCreate'
import { useImage } from '@/views/editor/composable/image/useImage.ts'
import { useImageCaption } from '@/views/editor/composable/image/useImageCaption.ts'
import { restoreLatexElements } from '@/views/editor/composable/useLatexRestore.ts'

let _tempBlocksCleanedAt = 0
let _pasteHandledAnchorUID = ''
let _pasteHandledAt = 0

class SocketIO {
	socket: Socket | any = null
	isConnected: boolean = false
	reconnectingByAuth: boolean = false
	private _duplicateDisconnect: boolean = false
	private originalEmit: Function | null = null
	private lastSocketResetTime: number = 0
	private _pendingSaveResolve: (() => void) | null = null
	private _pendingSaveTimer: ReturnType<typeof setTimeout> | null = null
	private _pendingTableUpdates: Map<string, number> = new Map()
	private _pendingCellUpdates: Map<string, number> = new Map()

	constructor() {
		this.socket = io({
			path: '/ws',
			autoConnect: false,
			transports: ['websocket'],
			auth: { token: localStorage.getItem('accessToken') ?? null },
			reconnection: true,
			reconnectionAttempts: 10,
			reconnectionDelay: 500,
			reconnectionDelayMax: 5000,
		})

		// 원본 emit 메서드 저장
		this.originalEmit = this.socket.emit.bind(this.socket)

		// emit 메서드를 로깅이 적용된 버전으로 교체
		this.socket.emit = (event: string, ...args: any[]) => {
			// 소켓 emit이 호출될 때마다 자동 로그아웃 타이머를 갱신한다
			// 단, 사용자 활동 이벤트와 중복되지 않도록 throttling 적용
			const now = Date.now()
			if (!this.lastSocketResetTime || now - this.lastSocketResetTime > 30000) {
				useCommonStore().setAutoLogout(useCommonStore().getAutoLogout())
				this.lastSocketResetTime = now
			}
			return this.originalEmit!(event, ...args)
		}

		// 연결 성공
		this.socket.off('connect')
		this.socket.on('connect', () => {
			this.isConnected = true
			this.reconnectingByAuth = false
		})

		// 서버가 "중복 로그인"으로 강제 종료하기 전에 알려줌
		this.socket.off('duplicate_connection')
		this.socket.on('duplicate_connection', () => {
			this._duplicateDisconnect = true
			localStorage.removeItem('accessToken')
			localStorage.removeItem('refreshToken')
			localStorage.removeItem('userName')
			window.location.href = '/sign-in'
		})

		// 연결 에러(핸드셰이크 단계) — 여기서 토큰 구해 재시도
		this.socket.off('connect_error')
		this.socket.on('connect_error', async (err: any) => {
			const msg = String(err?.message ?? '')

			// 1) 토큰이 안 왔음(NO_TOKEN): 로컬에 있으면 붙여서 즉시 재연결
			if (msg === 'NO_TOKEN') {
				const stored = localStorage.getItem('accessToken')
				if (stored) {
					this.socket!.auth = { token: stored }
					this.safeReconnect()
					return
				}
				// 없으면 로그인 화면
				this.goSignin()
				return
			}

			// 2) 토큰 만료/무효(INVALID_TOKEN): refresh 시도 → 성공 시 재연결, 실패 시 로그아웃
			if (msg === 'INVALID_TOKEN') {
				try {
					// 쿠키 기반 refresh 엔드포인트 (예: /api/auth/refresh)
					const data: any = await axiosRequest<any>('/v1/auth/refresh', {}, { withCredentials: true }, 'POST')
					// 서버 응답 형식에 맞게 꺼내 쓰세요
					const newAccess = data?.accessToken
					if (newAccess) {
						localStorage.setItem('accessToken', newAccess)
						this.socket!.auth = { token: newAccess }
						this.safeReconnect()
						return
					}
				} catch (error) {}
				this.goSignin()
				return
			}

			// 그 외 네트워크 등은 소켓 기본 재시도에 맡김
		})

		// 연결 끊김
		this.socket.off('disconnect')
		this.socket.on('disconnect', (reason: string) => {
			this.isConnected = false

			if (this._duplicateDisconnect) {
				return
			}

			// 서버가 명시적으로 끊은 경우(io server disconnect)는 수동 재연결 필요
			if (reason === 'io server disconnect') {
				// 토큰이 갱신되었을 수 있으니 다시 싱크
				const stored = localStorage.getItem('accessToken')
				this.socket!.auth = { token: stored ?? null }
				this.safeReconnect()
			}
			// 그 외(네트워크/서버재시작)는 자동 재연결 옵션이 처리
		})

		// 재연결 실패 (10회 모두 실패)
		this.socket.off('reconnect_failed')
		this.socket.on('reconnect_failed', () => {})
	}

	connect() {
		if (this.isConnected) return
		// 최신 토큰 싱크 후 연결
		this.socket!.auth = { token: localStorage.getItem('accessToken') ?? null }
		this.socket!.connect()
	}

	private safeReconnect() {
		if (this.reconnectingByAuth) return
		this.reconnectingByAuth = true
		// 약간의 지연을 두면 연속 connect_error 루프 줄어듦
		setTimeout(() => {
			this.socket?.connect()
		}, 50)
	}

	private goSignin() {
		localStorage.removeItem('accessToken')
		localStorage.removeItem('refreshToken')
		localStorage.removeItem('userName')
		window.location.href = '/sign-in'
	}

	noteParticipant(noteUid: string) {
		this.socket.emit('enter_note_participants', {
			noteUid: noteUid,
		})
	}

	table(params: any) {
		this.socket.emit('create_table', params)
	}

	leaveParticipant() {
		const { noteUid, participantsList } = storeToRefs(useEditorStore())
		this.socket.emit('leave_note_participants', {
			noteUid: noteUid.value,
		})
		participantsList.value = []
	}

	participantOn() {
		const { participantsList } = storeToRefs(useEditorStore())
		this.socket.off('retrieve_note_participants')
		this.socket.on('retrieve_note_participants', (data: any) => {
			participantsList.value = data
		})
	}

	createNoteBlock(noteUid: string, prevBlockUid: string, blockUid: string, content: string) {
		const { isLoadNote } = storeToRefs(useEditorStore())
		if (!isLoadNote.value) {
			const INVISIBLES = /[\u200B-\u200D\uFEFF\u00A0]/g
			if (content) {
				content = content.replace(INVISIBLES, '')
			}

			isLoadNote.value = true
			this.socket.emit('create-noteblock', {
				noteUID: noteUid,
				prevUID: prevBlockUid || null,
				tempBlockUID: blockUid || null,
				content: content || '',
			})
		}
	}

	createNoteBlockOn() {
		const { isLoadNote, selector, blockList, oldBlockUid, chartData, newBlockId, noteUid, undoStack, redoStack, isWrite } = storeToRefs(useEditorStore())
		this.socket.off('create-noteblock')
		this.socket.on('create-noteblock', async (data: any) => {
			console.log(data)
			if (data.tempBlockUID) {
				const oldElement: any = document.getElementById(data.tempBlockUID)

				if (oldElement) {
					oldElement.id = `prefix-${data.blockUID}`
					selector.value = oldElement
				}

				if (oldElement && oldElement.dataset.type === 'chart') {
					chartData.value.forEach((item: any) => {
						if (item.chartDataList.chartTargetUid === oldBlockUid.value) {
							item.chartDataList.chartTargetUid = data.blockUID

							this.modifyNoteBlock(noteUid.value, data.blockUID, JSON.stringify(item.chartDataList), 'chart')
						}
					})
				}

				// Update temp ID to real ID in history stacks (delta-based)
				const tempId = data.tempBlockUID
				const realId = data.blockUID

				undoStack.value.forEach((entry: any) => {
					if (entry.changes) {
						entry.changes.forEach((change: any) => {
							if (change.blockUid === tempId) change.blockUid = realId
						})
					}
					if (entry.beforeBlockOrder) {
						entry.beforeBlockOrder = entry.beforeBlockOrder.map((uid: string) => (uid === tempId ? realId : uid))
					}
					if (entry.afterBlockOrder) {
						entry.afterBlockOrder = entry.afterBlockOrder.map((uid: string) => (uid === tempId ? realId : uid))
					}
					if (entry.selection?.blockUid === tempId) entry.selection.blockUid = realId
					if (entry.beforeSelection?.blockUid === tempId) entry.beforeSelection.blockUid = realId
				})

				redoStack.value.forEach((entry: any) => {
					if (entry.changes) {
						entry.changes.forEach((change: any) => {
							if (change.blockUid === tempId) change.blockUid = realId
						})
					}
					if (entry.beforeBlockOrder) {
						entry.beforeBlockOrder = entry.beforeBlockOrder.map((uid: string) => (uid === tempId ? realId : uid))
					}
					if (entry.afterBlockOrder) {
						entry.afterBlockOrder = entry.afterBlockOrder.map((uid: string) => (uid === tempId ? realId : uid))
					}
					if (entry.selection?.blockUid === tempId) entry.selection.blockUid = realId
					if (entry.beforeSelection?.blockUid === tempId) entry.beforeSelection.blockUid = realId
				})

				updateBaselineBlockId(tempId, realId)

				oldBlockUid.value = ''
				newBlockId.value = data.blockUID

				isLoadNote.value = false
			} else {
				blockList.value.push({
					blockUid: data.blockUID,
					content: '',
					noteUid: data.noteUID,
					projectUid: '',
					reviewCnt: 0,
					sortOrder: 0,
					status: 0,
					type: 'text',
				})

				const blockWrap = document.querySelector('.editor--container__content')
				const newBlockContainer = document.createElement('div')
				newBlockContainer.classList.add('editor--block__container')
				newBlockContainer.contentEditable = 'false'

				const newBlockElement = document.createElement('div')
				newBlockElement.classList.add('editor--block')
				newBlockElement.id = `prefix-${data.blockUID}`
				newBlockElement.dataset.type = 'text'
				newBlockElement.setAttribute('tabIndex', '1')
				newBlockElement.setAttribute('contenteditable', isWrite.value ? 'true' : 'false')
				newBlockElement.innerHTML = ''

				newBlockContainer.appendChild(newBlockElement)

				if (data.prevUID) {
					const prevBlock = document.getElementById(`prefix-${data.prevUID}`)
					const prevContainer = prevBlock?.closest('.editor--block__container')
					if (prevContainer?.nextSibling) {
						blockWrap?.insertBefore(newBlockContainer, prevContainer.nextSibling)
					} else {
						blockWrap?.appendChild(newBlockContainer)
					}
				} else {
					blockWrap?.appendChild(newBlockContainer)
				}

				newBlockEvent()
				isLoadNote.value = false

				// 초기 블록 생성 후 히스토리 재초기화 (빈 S0 방지)
				await nextTick()
				const history = new UseHistory()
				history.reset()
				await history.push()
			}
		})
	}

	createNoteBlockRetrieveOn() {
		this.socket.off('retrieve-created-noteblock')
		this.socket.on('retrieve-created-noteblock', (data: any) => {
			const { isWrite } = storeToRefs(useEditorStore())

			if (document.getElementById(`prefix-${data.blockUID}`)) return

			const newDivContainer = document.createElement('div')
			newDivContainer.classList.add('editor--block__container')
			newDivContainer.contentEditable = 'false'
			const newDiv = document.createElement('div')
			newDiv.id = `prefix-${data.blockUID}`
			newDiv.classList.add('editor--block')
			newDiv.dataset.type = data.blockType || 'text'
			newDiv.setAttribute('contenteditable', isWrite.value ? 'true' : 'false')
			newDiv.setAttribute('tabIndex', '1')
			newDiv.innerHTML = data.content || ''

			newDivContainer.appendChild(newDiv)

			const blockWrap = document.querySelector('.editor--container__content')

			if (data.prevUID) {
				const prevBlock = document.getElementById(`prefix-${data.prevUID}`)
				if (prevBlock) {
					const prevContainer = prevBlock.closest('.editor--block__container')
					prevContainer?.insertAdjacentElement('afterend', newDivContainer)
				} else {
					blockWrap?.appendChild(newDivContainer)
				}
			} else {
				blockWrap?.appendChild(newDivContainer)
			}

			newBlockEvent()

			updateBaselineForRemoteCreate(data.blockUID, data.content || '', data.blockType || 'text', data.prevUID)
		})
	}

	async modifyNoteBlock(noteUid: string, blockUid: string, content: string, type: string = 'text') {
		const { isFocusOut } = storeToRefs(useEditorStore())

		if (blockUid.startsWith('temp')) {
			return
		}

		const INVISIBLES = /[\u200B-\u200D\uFEFF\u00A0]/g
		if (type !== 'chart') {
			content = content.replace(INVISIBLES, '')
		}
		useImage().deleteSizeElement()
		this.socket.emit('update-noteblock', {
			noteUID: noteUid,
			blockUID: blockUid,
			blockType: type,
			content: content,
		})
		await nextTick()
		isFocusOut.value = false
	}

	modifyNoteBlockOn() {
		const { chartData, selector, isImageSize, isImageResize } = storeToRefs(useEditorStore())
		this.socket.off('update-noteblock')
		this.socket.on('update-noteblock', async (data: any) => {
			this.resolvePendingSave()
			const pasteKey = `${data.noteUID}-${data.blockUID}`

			if (recentlyPastedBlocks.has(pasteKey)) {
				return
			}

			const editorBlockList: any = document.querySelector(`.editor--block#prefix-${data.blockUID}`)
			if (!editorBlockList) {
				return
			}
			new UseSelection().setChangeSelection()
			if (data.blockType === 'chart') {
				chartData.value.forEach((item: any) => {
					if (item.chartDataList.chartTargetUid === data.blockUID) {
						const chartType = {
							blockUid: data.blockUID,
							chartDataList: typeof item.chartDataList === 'string' ? JSON.parse(item.chartDataList) : item.chartDataList,
						}
						item.blockUid = data.blockUID

						chartData.value.push(chartType)
					}
				})

				useChart().init()
			} else if (data.blockType === 'defaultTable') {
				const activeEl = document.activeElement as HTMLElement | null
				const isEditingTable = activeEl && (activeEl === editorBlockList || editorBlockList.contains(activeEl))
				if (isEditingTable) {
					return
				}
				try {
					const content = typeof data.content === 'string' ? JSON.parse(data.content) : data.content
					if (content) {
						const tableUid = content.tableUID || content.tableUid
						if (tableUid) {
							content.tableUID = tableUid
							content.tableUid = tableUid
						}
					}
					editorBlockList.innerHTML = String(useCreateTable().insertTable(content, data.blockUID))
					editorBlockList.dataset.type = 'defaultTable'
					editorBlockList.setAttribute('contenteditable', 'false')
					await nextTick()
					const newTable = editorBlockList.querySelector('table')
					if (newTable) {
						useCreateTable().tableShapes(newTable)
						useCreateTable().widthSizeItem(newTable)
						useCreateTable().heightSizeItem(newTable)
					}
					useCreateTable().event(editorBlockList)
				} catch {
					editorBlockList.innerHTML = ''
				}
			} else if (data.blockType === 'file') {
				editorBlockList.innerHTML = ''
				editorBlockList.dataset.type = 'file'
				const content = typeof data.content === 'string' ? JSON.parse(data.content) : data.content
				editorBlockList.appendChild(addFileBlock(content))

				const blockFileList = editorBlockList.querySelectorAll('.file--content__list > div')
				blockFileList.forEach((blockFile: any) => {
					blockFile.addEventListener('click', downloadFile)
				})

				const blockFileDelete = editorBlockList.querySelectorAll('.file--content__list > button')
				blockFileDelete.forEach((blockFile: any) => {
					blockFile.addEventListener('click', deleteFile)
				})

				const blockFileAllDelete: any = editorBlockList.querySelector('.file--title button')
				blockFileAllDelete.addEventListener('click', allDeleteFile)
			} else if (data.blockType === 'image') {
				if (isImageSize.value || isImageResize.value) {
					return
				}

				const activeEl = document.activeElement as HTMLElement | null
				const isEditingCaption = activeEl && editorBlockList.contains(activeEl) && (activeEl.closest('.image--caption__title') || activeEl.closest('.image--caption__description'))
				if (isEditingCaption) {
					return
				}

				editorBlockList.innerHTML = data.content
				editorBlockList.dataset.type = 'image'
				editorBlockList.setAttribute('contenteditable', 'false')

				const imgEl = editorBlockList.querySelector('img')
				if (imgEl) {
					imgEl.setAttribute('draggable', 'false')
					imgEl.addEventListener('dragstart', (e: any) => e.preventDefault())
				}

				useImageCaption().rebindCaptionEvents(editorBlockList)

				const { enterSelector } = storeToRefs(useEditorStore())
				enterSelector.value = editorBlockList
				const uImage = useImage()
				uImage.reInit()

				newBlockEvent()
			} else {
				if (selector.value && selector.value.id === editorBlockList.id) {
					const isLockedByOther = editorBlockList.getAttribute('data-avatar-locked') === 'true'
					if (!isLockedByOther) {
						const activeEl = document.activeElement as HTMLElement | null
						const isActivelyEditing = activeEl === editorBlockList || editorBlockList.contains(activeEl as Node) || (activeEl?.id === 'editor' && activeEl.contains(editorBlockList))
						if (isActivelyEditing) {
							return
						}
					}
				}
				editorBlockList.innerHTML = data.content
				restoreLatexElements(editorBlockList)
			}
			const activeAfter = document.activeElement as HTMLElement | null
			const stillInBlock = activeAfter && (activeAfter === editorBlockList || editorBlockList.contains(activeAfter))
			if (stillInBlock) {
				new UseSelection().isCursorCheckPoint()
			}

			// 베이스라인 갱신: 원격 블럭 수정을 히스토리 베이스라인에 반영
			if (data.blockType === 'defaultTable') {
				try {
					const table = editorBlockList.querySelector('table')
					if (table) {
						updateBaselineForRemoteTableBlock(data.blockUID, setTableData(table))
					}
				} catch (e) {}
			} else {
				updateBaselineForRemoteModify(data.blockUID, editorBlockList.innerHTML, data.blockType)
			}
		})
	}

	deleteNoteBlock(noteUid: string, blockUid: string) {
		this.socket.emit('remove-noteblock', {
			noteUID: noteUid,
			blockUID: blockUid,
		})
	}

	deleteNoteBlockOn() {
		this.socket.off('remove-noteblock')
		this.socket.on('remove-noteblock', async (data: any) => {
			const dom: any = document.getElementById(`prefix-${data.blockUID}`)
			if (dom) {
				dom.closest('.editor--block__container')?.remove()
			}

			updateBaselineForRemoteDelete(data.blockUID)

			const blockList = document.querySelectorAll('.editor--block__container')
			if (blockList.length < 1) {
				await createBlockEvent()
				const { selector } = storeToRefs(useEditorStore())
				if (selector.value) {
					selector.value.focus()
				}
			}
		})
	}

	deleteNoteBlockList(noteUid: string, blockUidList: string[]) {
		this.socket.emit('remove-noteblock-list', {
			noteUID: noteUid,
			blockUIDs: blockUidList,
		})
	}

	deleteNoteBlockListOn() {
		this.socket.off('remove-noteblock-list')
		this.socket.on('remove-noteblock-list', async (data: any) => {
			const blockUidList = data.blockUIDs
			if (Array.isArray(blockUidList)) {
				blockUidList.forEach((blockUid: string) => {
					const dom: any = document.getElementById(`prefix-${blockUid}`)
					if (dom) {
						dom.closest('.editor--block__container')?.remove()
					}
					updateBaselineForRemoteDelete(blockUid)
				})
			}

			const blockList = document.querySelectorAll('.editor--block__container')
			if (blockList.length < 1) {
				await createBlockEvent()
				const { selector } = storeToRefs(useEditorStore())
				if (selector.value) {
					selector.value.focus()
				}
			}
		})
	}

	createTableBlock(params: any) {
		const blockUID = params.blockUID || params.blockUid || ''
		if (blockUID) {
			this._pendingTableUpdates.set(blockUID, (this._pendingTableUpdates.get(blockUID) || 0) + 1)
		}
		this.socket.emit('update-table', params)
	}

	createTableBlockOn() {
		// 기존 핸들러 제거 (중복 등록 방지)
		this.socket.off('update-table')

		this.socket.on('update-table', async (data: any) => {
			this.resolvePendingSave()
			const blockUID = data.blockUID || ''

			// 빈 blockUID로 전송된 신규 테이블 생성의 경우,
			// 서버 응답의 real blockUID에 대해 pending count가 없으므로
			// 이후 도착할 retrieve-updated-table이 보호되지 않는 문제를 방지
			if (blockUID && !this._pendingTableUpdates.has(blockUID)) {
				this._pendingTableUpdates.set(blockUID, 1)
			}

			let blockListData: any = document.querySelector(`.editor--block#prefix-${blockUID}`)

			if (!blockListData && blockUID) {
				const { oldBlockUid, newBlockId, selector, undoStack, redoStack } = storeToRefs(useEditorStore())
				if (oldBlockUid.value) {
					blockListData = document.getElementById(oldBlockUid.value)
					if (blockListData) {
						const tempId = oldBlockUid.value
						blockListData.id = `prefix-${blockUID}`

						undoStack.value.forEach((entry: any) => {
							if (entry.changes) {
								entry.changes.forEach((change: any) => {
									if (change.blockUid === tempId) change.blockUid = blockUID
								})
							}
							if (entry.beforeBlockOrder) {
								entry.beforeBlockOrder = entry.beforeBlockOrder.map((uid: string) => (uid === tempId ? blockUID : uid))
							}
							if (entry.afterBlockOrder) {
								entry.afterBlockOrder = entry.afterBlockOrder.map((uid: string) => (uid === tempId ? blockUID : uid))
							}
							if (entry.selection?.blockUid === tempId) entry.selection.blockUid = blockUID
							if (entry.beforeSelection?.blockUid === tempId) entry.beforeSelection.blockUid = blockUID
						})

						redoStack.value.forEach((entry: any) => {
							if (entry.changes) {
								entry.changes.forEach((change: any) => {
									if (change.blockUid === tempId) change.blockUid = blockUID
								})
							}
							if (entry.beforeBlockOrder) {
								entry.beforeBlockOrder = entry.beforeBlockOrder.map((uid: string) => (uid === tempId ? blockUID : uid))
							}
							if (entry.afterBlockOrder) {
								entry.afterBlockOrder = entry.afterBlockOrder.map((uid: string) => (uid === tempId ? blockUID : uid))
							}
							if (entry.selection?.blockUid === tempId) entry.selection.blockUid = blockUID
							if (entry.beforeSelection?.blockUid === tempId) entry.beforeSelection.blockUid = blockUID
						})

						updateBaselineBlockId(tempId, blockUID)

						if (selector.value && selector.value.id === `prefix-${blockUID}`) {
							selector.value = blockListData
						}

						oldBlockUid.value = ''
						newBlockId.value = blockUID
					}
				}
			}

			if (!blockListData) return
			const table = blockListData.querySelector('table')

			try {
				const content = typeof data.content === 'string' ? JSON.parse(data.content) : data.content
				if (content) {
					const tableUid = data.tableUID || content.tableUid || content.tableUID
					if (tableUid) {
						table.id = addPrefix(tableUid)
					}

					content.rows.forEach((row: any, rowIndex: number) => {
						row.forEach((cell: any, cellIndex: number) => {
							const targetCell = table.rows[rowIndex + 1]?.cells[cellIndex + 1]
							if (targetCell && cell.cellUid) {
								targetCell.id = addPrefix(cell.cellUid)
							}
						})
					})
				}
			} catch (e) {
				console.log(e)
			}
		})
	}

	createViewerTableBlockOn() {
		// 기존 핸들러 제거 (중복 등록 방지)
		this.socket.off('retrieve-updated-table')

		const { blockList } = storeToRefs(useEditorStore())
		this.socket.on('retrieve-updated-table', async (data: any) => {
			const blockUID = data.blockUID || ''
			const pendingCount = this._pendingTableUpdates.get(blockUID) || 0
			if (pendingCount > 0) {
				this._pendingTableUpdates.set(blockUID, pendingCount - 1)
				if (pendingCount - 1 <= 0) this._pendingTableUpdates.delete(blockUID)
				return
			}

			const { isUndoRedoInProgress, lastUndoRedoTime } = storeToRefs(useEditorStore())
			if (isUndoRedoInProgress.value) return
			if (Date.now() - lastUndoRedoTime.value < 500) return

			const blockListData: any = document.querySelector(`.editor--block#prefix-${data.blockUID}`)
			if (!blockListData) return

			const activeEl = document.activeElement as HTMLElement | null
			const isEditingTable = activeEl && (activeEl === blockListData || blockListData.contains(activeEl))
			if (isEditingTable) {
				return
			}

			// innerHTML 재할당 전에 이벤트 플래그 제거 (WeakSet + dataset 모두 제거)
			useCreateTable().clearEventFlags(blockListData)

			try {
				const content = typeof data.content === 'string' ? JSON.parse(data.content) : data.content
				if (content) {
					const tableUid = content.tableUID || content.tableUid || data.tableUID
					if (tableUid) {
						content.tableUID = tableUid
						content.tableUid = tableUid
					}
				}
				const tableHtml = useCreateTable().insertTable(content, data.blockUID)
				if (tableHtml) {
					blockListData.innerHTML = tableHtml
				}
			} catch {
				// 에러 시 기존 테이블 유지 (innerHTML 초기화 방지)
			}
			blockListData.dataset.type = 'defaultTable'
			blockListData?.setAttribute('contenteditable', 'false')

			// 이벤트 핸들러 재등록
			await nextTick()

			// 테이블 수식 셀 재계산
			const table = blockListData.querySelector('table')
			if (table) {
				useCreateTable().tableShapes(table)
				useCreateTable().widthSizeItem(table)
				useCreateTable().heightSizeItem(table)
			}
			calcFunction()

			await useCreateTable().event(blockListData)

			try {
				const updatedTable = blockListData.querySelector('table')
				if (updatedTable) {
					updateBaselineForRemoteTableBlock(data.blockUID, setTableData(updatedTable))
				}
			} catch (e) {}
		})
	}

	updateCell(noteUid: string, blockUid: string, tableUid: string, cellUid: string, content: any) {
		if (cellUid) {
			this._pendingCellUpdates.set(cellUid, (this._pendingCellUpdates.get(cellUid) || 0) + 1)
		}
		this.socket.emit('update-cell', {
			noteUID: noteUid,
			blockUID: blockUid,
			tableUID: tableUid,
			cellUID: cellUid,
			content: content,
		})
	}

	updateCellOn() {
		this.socket.off('retrieve-updated-cell')

		this.socket.on('retrieve-updated-cell', (data: any) => {
			this.resolvePendingSave()
			const cellUID = data.cellUID || ''
			const pendingCount = this._pendingCellUpdates.get(cellUID) || 0
			if (pendingCount > 0) {
				this._pendingCellUpdates.set(cellUID, pendingCount - 1)
				if (pendingCount - 1 <= 0) this._pendingCellUpdates.delete(cellUID)
				updateBaselineForRemoteCellUpdate(data.blockUID, data.cellUID, data.content.content, data.content.dataType)
				return
			}

			const { isUndoRedoInProgress, lastUndoRedoTime } = storeToRefs(useEditorStore())
			if (isUndoRedoInProgress.value) return
			if (Date.now() - lastUndoRedoTime.value < 500) return

			const changeCell = document.querySelector(`.editor--block#prefix-${data.blockUID} #prefix-${data.cellUID}`)
			if (!changeCell) return

			Object.entries(data.content.dataType).forEach(([key, value]) => {
				if (key !== 'colSpan' && key !== 'rowSpan' && key !== 'width' && key !== 'height') {
					if (typeof value === 'string') {
						changeCell.setAttribute(`data-${key}`, value)
					}
				}
			})
			const cellDiv = changeCell.querySelector('div') as HTMLElement | null
			if (!cellDiv) return

			if (cellDiv.innerHTML !== data.content.content) {
				cellDiv.innerHTML = data.content.content
			}

			updateBaselineForRemoteCellUpdate(data.blockUID, data.cellUID, data.content.content, data.content.dataType)
		})
	}

	createTemplate(noteUid: string, blockUid: string, templateUid: string, approvalVersion: string, generalVersion: string) {
		this.socket.emit('create-template', {
			noteUID: noteUid,
			templateUID: templateUid,
			blockUID: blockUid,
			version: approvalVersion,
		})
	}

	createTemplateOn() {
		this.socket.off('create-template')
		this.socket.on('create-template', async (data: any) => {
			const { blockUID, noteBlockList } = data
			let blockElement: any = document.getElementById(`prefix-${blockUID}`)
			let lastCreatedBlock: HTMLElement | null = null
			const initialBlock = blockElement

			noteBlockList.forEach((item: any) => {
				const blockType = item.type || item.blockType || 'text'

				if (blockUID === item.blockUid) {
					if (blockType === 'defaultTable') {
						blockElement.dataset.type = 'defaultTable'
						blockElement.setAttribute('contenteditable', 'false')
						try {
							const content = typeof item.content === 'string' ? JSON.parse(item.content) : item.content
							const tableUid = content?.tableUID || content?.tableUid
							if (content && tableUid) {
								content.tableUID = tableUid
								content.tableUid = tableUid
							}
							blockElement.innerHTML = String(useCreateTable().insertTable(content, item.blockUid))
						} catch {
							blockElement.innerHTML = ''
						}
					} else {
						blockElement.innerHTML = item.content
					}
					lastCreatedBlock = blockElement
				} else {
					const newDivContainer = document.createElement('div')
					newDivContainer.classList.add('editor--block__container')
					const newDiv = document.createElement('div')
					newDiv.id = `prefix-${item.blockUid}`
					newDiv.classList.add('editor--block')

					if (blockType === 'defaultTable') {
						newDiv.dataset.type = 'defaultTable'
						newDiv.setAttribute('contenteditable', 'false')
						try {
							const content = typeof item.content === 'string' ? JSON.parse(item.content) : item.content
							const tableUid = content?.tableUID || content?.tableUid
							if (content && tableUid) {
								content.tableUID = tableUid
								content.tableUid = tableUid
							}
							newDiv.innerHTML = String(useCreateTable().insertTable(content, item.blockUid))
						} catch {
							newDiv.innerHTML = ''
						}
					} else if (blockType === 'image') {
						newDiv.dataset.type = 'image'
						newDiv.setAttribute('contenteditable', 'false')
						newDiv.innerHTML = item.content
					} else {
						newDiv.dataset.type = 'text'
						newDiv.setAttribute('contenteditable', 'true')
						newDiv.innerHTML = item.content
					}

					newDivContainer.appendChild(newDiv)

					blockElement.closest('.editor--block__container').insertAdjacentElement('afterend', newDivContainer)
					blockElement = newDivContainer
					lastCreatedBlock = newDiv
				}
			})

			newBlockEvent()

			// 마지막 블록으로 캐럿 이동 (새 블록이 없으면 초기 블록 사용)
			const targetBlock = (lastCreatedBlock || initialBlock) as HTMLElement
			if (targetBlock) {
				const { selector } = storeToRefs(useEditorStore())

				await nextTick()
				await new Promise(requestAnimationFrame)

				// 기존 active 클래스 제거
				document.querySelectorAll('.editor--block.active').forEach(block => {
					block.classList.remove('active')
				})

				const isTable = targetBlock.dataset.type === 'defaultTable'
				if (!isTable) {
					targetBlock.classList.add('active')
					targetBlock.focus()

					await nextTick()
					await new Promise(requestAnimationFrame)

					const sel = new UseSelection()
					sel.setElement(targetBlock)
					sel.moveCursorToEnd()
				}
				selector.value = targetBlock
			}
		})
	}

	createRetrieveTemplateOn() {
		const { blockList } = storeToRefs(useEditorStore())
		this.socket.off('retrieve-created-template')
		this.socket.on('retrieve-created-template', (data: any) => {
			const { blockUID, noteBlockList } = data
			let blockIndex = 0
			blockList.value.forEach((item: any, index: number) => {
				if (item.blockUid === blockUID) {
					blockIndex = index
				}
			})
			noteBlockList.forEach((item: any) => {
				const params = {
					blockUid: item.blockUid,
					templateUid: item.templateUid,
					content: item.content,
					type: item.type,
				}
				blockList.value.splice(blockIndex, 0, params)
				blockIndex++
			})
		})
	}

	updateFile(noteUid: string, blockUid: string) {
		this.socket.emit('update-file', {
			noteUID: noteUid,
			blockUID: blockUid,
		})
	}

	updateFileOn() {
		const { blockList } = storeToRefs(useEditorStore())
		this.socket.off('retrieve-updated-file')
		this.socket.on('retrieve-updated-file', (data: any) => {
			blockList.value.forEach((item: any, index: number) => {
				if (item.blockUid === data.blockUID) {
					item.content = data.content
				}
			})
		})
	}

	pasteBlock(blockUid: string, blockData: any) {
		const { noteUid } = storeToRefs(useEditorStore())
		this.socket.emit('paste-blocks', {
			noteUID: noteUid.value,
			blockUID: blockUid,
			blocks: blockData,
		})
	}
	pasteBlockOn() {
		this.socket.off('paste-blocks')

		this.socket.on('paste-blocks', async (data: any) => {
			const { blockUID, blocks } = data
			console.log(data)
			console.log('[pasteBlockOn] 수신 blockUID:', blockUID, 'blocks:', blocks?.length)

			if (!blocks || !Array.isArray(blocks)) {
				const { isPasting } = storeToRefs(useEditorStore())
				isPasting.value = false
				return
			}

			let editorBlock: any = document.getElementById(`prefix-${blockUID}`) || document.getElementById(blockUID)
			console.log('[pasteBlockOn] editorBlock:', editorBlock?.id)
			if (!editorBlock) {
				// deleteNoteBlockList 응답으로 기준 블록이 이미 삭제된 경우
				// temp-가 아닌 마지막 블록 컨테이너를 기준으로 삼음
				const allBlocks = [...document.querySelectorAll('.editor--block')].filter((b: any) => !b.id.startsWith('temp-'))
				const lastValidBlock: any = allBlocks[allBlocks.length - 1]
				if (!lastValidBlock) {
					console.error('[pasteBlockOn] editorBlock not found and no fallback:', blockUID)
					return
				}
				editorBlock = lastValidBlock
			}

			// 이미 처리된 paste인지 확인 (retrieve에서 먼저 처리된 경우)
			const recentlyHandledByRetrieve = Date.now() - _tempBlocksCleanedAt < 1000
			if (recentlyHandledByRetrieve) {
				document.querySelectorAll('.editor--block').forEach((item: any) => {
					if (item.id.includes('temp')) {
						item.closest('.editor--block__container')?.remove()
					}
				})
				const { isPasting } = storeToRefs(useEditorStore())
				isPasting.value = false
				return
			}

			let tmpBlockContainer: any = editorBlock.closest('.editor--block__container')

			let anchorContainer: any = tmpBlockContainer
			while (anchorContainer) {
				const innerBlock = anchorContainer.querySelector('.editor--block')
				if (!innerBlock || !innerBlock.id.includes('temp')) {
					break
				}
				anchorContainer = anchorContainer.previousElementSibling
			}

			const removeBlockList: any = document.querySelectorAll('.editor--block')

			removeBlockList.forEach((item: any) => {
				if (item.id.includes('temp')) {
					item.closest('.editor--block__container')?.remove()
				}
			})

			// 블록 삽입 시작 전에 즉시 처리 완료 표시 (retrieve 중복 방지)
			_pasteHandledAnchorUID = blockUID
			_pasteHandledAt = Date.now()

			tmpBlockContainer = anchorContainer || document.getElementById('editor')
			let lastNonEmptyBlock: any = null

			blocks.forEach((item: any) => {
				const newDivContainer = document.createElement('div')
				newDivContainer.classList.add('editor--block__container')
				const newDiv = document.createElement('div')
				newDiv.id = `prefix-${item.blockUID}`
				newDiv.classList.add('editor--block')
				const blockType = item.type || item.blockType || 'text'
				if (blockType === 'defaultTable') {
					newDiv.dataset.type = 'defaultTable'
					newDiv.setAttribute('contenteditable', 'false')
					try {
						// 서버가 tableUid/tableUID 중 어떤 키로 주더라도 insertTable이 동작하도록 보정
						const content = typeof item.content === 'string' ? JSON.parse(item.content) : item.content
						const tableUid = content?.tableUID || content?.tableUid
						if (content && tableUid) {
							content.tableUID = tableUid
							content.tableUid = tableUid
						}
						newDiv.innerHTML = String(useCreateTable().insertTable(content, item.blockUID))
						console.log(newDiv.innerHTML)
					} catch {
						newDiv.innerHTML = ''
					}
					lastNonEmptyBlock = newDiv
				} else if (blockType === 'image') {
					newDiv.dataset.type = 'image'
					newDiv.setAttribute('contenteditable', 'false')
					newDiv.innerHTML = item.content
					lastNonEmptyBlock = newDiv
				} else {
					newDiv.dataset.type = 'text'
					newDiv.setAttribute('contenteditable', 'true')
					newDiv.innerHTML = item.content
					// 빈 블록이 아닌 경우에만 lastNonEmptyBlock 갱신
					const tempDiv = document.createElement('div')
					tempDiv.innerHTML = item.content
					const textContent = tempDiv.textContent || ''
					const normalizedContent = textContent.replace(/[\s\u00A0\u200B-\u200D\uFEFF]/g, '')
					if (normalizedContent && normalizedContent.trim() !== '') {
						lastNonEmptyBlock = newDiv
					}
				}

				newDivContainer.appendChild(newDiv)

				tmpBlockContainer.insertAdjacentElement('afterend', newDivContainer)
				tmpBlockContainer = newDivContainer
			})

			const previewBlock: any = document.querySelector('[data-paste-preview]')
			if (previewBlock) {
				previewBlock.removeAttribute('data-paste-preview')
				previewBlock.closest('.editor--block__container')?.remove()
			}

			newBlockEvent()
			// 테이블이 포함된 경우 테이블 이벤트 재적용 (새로 추가된 테이블만)
			try {
				const allTables = document.querySelectorAll('.editor--block[data-type="defaultTable"]')
				for (const table of Array.from(allTables)) {
					const tableElement = table as HTMLElement
					if (!tableElement.dataset.eventsAttached) {
						await useCreateTable().event(tableElement)
					}
				}
			} catch {}

			// 마지막 비어있지 않은 블록으로 포커스 이동
			if (lastNonEmptyBlock) {
				const { selector } = storeToRefs(useEditorStore())
				selector.value = lastNonEmptyBlock
				await nextTick()
				const allBlocks = document.querySelectorAll('.editor--block')
				allBlocks.forEach((block: any) => {
					block.classList.remove('active')
				})

				if (lastNonEmptyBlock.dataset.type === 'text') {
					// 텍스트 블록이면 커서 유지
					lastNonEmptyBlock.classList.add('active')
					lastNonEmptyBlock.focus()

					const range = document.createRange()
					const sel = window.getSelection()
					if (lastNonEmptyBlock.lastChild) {
						range.setStartAfter(lastNonEmptyBlock.lastChild)
					} else {
						range.selectNodeContents(lastNonEmptyBlock)
					}
					range.collapse(false)
					sel?.removeAllRanges()
					sel?.addRange(range)
				} else {
					// 테이블/이미지 등이면 선택 해제
					window.getSelection()?.removeAllRanges()
					;(document.activeElement as HTMLElement)?.blur()
				}
			}

			const { isPasting } = storeToRefs(useEditorStore())
			isPasting.value = false
		})
	}

	pasteRetrieveBlockOn() {
		this.socket.off('retrieve-paste-blocks')
		this.socket.on('retrieve-paste-blocks', async (data: any) => {
			const { blockUID, blocks } = data

			if (!blocks || !Array.isArray(blocks)) {
				return
			}

			const editorBlock: any = document.getElementById(`prefix-${blockUID}`) || document.getElementById(blockUID)
			if (!editorBlock) {
				return
			}

			await new Promise<void>(resolve => setTimeout(resolve, 300))

			if (_pasteHandledAnchorUID === blockUID && Date.now() - _pasteHandledAt < 2000) {
				return
			}

			const stillExists = document.getElementById(`prefix-${blockUID}`) || document.getElementById(blockUID)
			if (!stillExists) return

			// pasteBlockOn이 이미 블록을 삽입했으면 중복 방지
			const alreadyInserted = blocks.every((item: any) => !!document.getElementById(`prefix-${item.blockUID}`))
			if (alreadyInserted && blocks.length > 0) return

			let tmpBlockContainer: any = stillExists.closest('.editor--block__container')

			blocks.forEach((item: any) => {
				const existingBlock = document.getElementById(`prefix-${item.blockUID}`)
				if (existingBlock) {
					tmpBlockContainer = (existingBlock.closest('.editor--block__container') as HTMLElement) || tmpBlockContainer
					return
				}

				const newDivContainer = document.createElement('div')
				newDivContainer.classList.add('editor--block__container')
				const newDiv = document.createElement('div')
				newDiv.id = `prefix-${item.blockUID}`
				newDiv.classList.add('editor--block')
				const blockType = item.type || item.blockType || 'text'
				if (blockType === 'defaultTable') {
					newDiv.dataset.type = 'defaultTable'
					newDiv.setAttribute('contenteditable', 'false')
					try {
						const content = typeof item.content === 'string' ? JSON.parse(item.content) : item.content
						const tableUid = content?.tableUID || content?.tableUid
						if (content && tableUid) {
							content.tableUID = tableUid
							content.tableUid = tableUid
						}
						newDiv.innerHTML = String(useCreateTable().insertTable(content, item.blockUID))
					} catch {
						newDiv.innerHTML = ''
					}
				} else if (blockType === 'image') {
					newDiv.dataset.type = 'image'
					newDiv.setAttribute('contenteditable', 'false')
					newDiv.innerHTML = item.content
				} else {
					newDiv.dataset.type = 'text'
					newDiv.setAttribute('contenteditable', 'true')
					newDiv.innerHTML = item.content
				}

				newDivContainer.appendChild(newDiv)

				tmpBlockContainer.insertAdjacentElement('afterend', newDivContainer)
				tmpBlockContainer = newDivContainer
			})
			_pasteHandledAnchorUID = blockUID
			_pasteHandledAt = Date.now()

			try {
				const allTables = document.querySelectorAll('.editor--block[data-type="defaultTable"]')
				for (const table of Array.from(allTables)) {
					const tableElement = table as HTMLElement
					if (!tableElement.dataset.eventsAttached) {
						await useCreateTable().event(tableElement)
					}
				}
			} catch {}
		})
	}

	comment(noteUid: string, blockUid: string, content: string) {
		this.socket.emit('create-review', {
			noteUID: noteUid,
			blockUID: blockUid,
			content: content,
		})
	}

	modifyComment(noteUid: string, blockUid: string, reviewUid: string, content: string) {
		this.socket.emit('update-review', {
			noteUID: noteUid,
			blockUID: blockUid,
			reviewUID: reviewUid,
			content: content,
		})
	}

	deleteComment(noteUid: string, blockUid: string, reviewUid: string) {
		this.socket.emit('delete-review', {
			noteUID: noteUid,
			blockUID: blockUid,
			reviewUID: reviewUid,
		})
	}

	commentOn() {
		const { reviewList } = storeToRefs(useEditorStore())
		this.socket.off('retrieve-review')
		this.socket.on('retrieve-review', (data: any) => {
			const { blockUID, reviewCnt, review } = data
			reviewList.value = review
			const editorBlockList: any = document.querySelector(`.editor--block#prefix-${blockUID}`)
			const container = editorBlockList.closest('.editor--block__container')

			const commentContainer = container.querySelector('.comment--container')
			if (commentContainer) {
				commentContainer.remove()
			}
			const newDiv = document.createElement('div')
			newDiv.classList.add('comment--container')
			newDiv.dataset.setid = blockUID
			newDiv.setAttribute('contenteditable', 'false')
			newDiv.innerHTML = `<span data-testid="editor-comment-count">${reviewCnt}</span> <i class="ico ico--comment" data-testid="editor-comment-icon"></i>`
			container.appendChild(newDiv)
		})
	}
	retrieveMentionCount(onMentionReceived?: (payload: any) => void) {
		this.socket.off('retrieve-mention')
		this.socket.on('retrieve-mention', (payload: any) => {
			// 콜백이 있으면 호출
			if (onMentionReceived) {
				onMentionReceived(payload)
			}
		})
	}
	projectResumeRequest(noteUid: string | null, userInfo: any, emit: any = null) {
		const { lang } = storeToRefs(useCommonStore())
		if (!noteUid) useCommonStore().open('error', lang.value.modal.error.title, lang.value.modal.error.noteUid, null)
		if (!userInfo) useCommonStore().open('error', lang.value.modal.error.title, lang.value.modal.error.userInfo, null)
		if (noteUid && userInfo) {
			this.socket.emit('request-signature', {
				noteUid: { noteUid: noteUid },
				calledIds: userInfo,
			})
			const callback = typeof emit === 'function' ? () => emit('close') : undefined
			return useCommonStore().open('default', lang.value.modal.error.title, lang.value.modal.success.signatureRequest, callback)
		}
	}

	waitForSave(timeout: number = 2000): Promise<void> {
		return new Promise(resolve => {
			if (this._pendingSaveTimer) {
				clearTimeout(this._pendingSaveTimer)
			}

			this._pendingSaveResolve = () => {
				if (this._pendingSaveTimer) {
					clearTimeout(this._pendingSaveTimer)
					this._pendingSaveTimer = null
				}
				this._pendingSaveResolve = null
				resolve()
			}

			this._pendingSaveTimer = setTimeout(() => {
				if (this._pendingSaveResolve) {
					this._pendingSaveResolve = null
					this._pendingSaveTimer = null
					resolve()
				}
			}, timeout)
		})
	}

	resolvePendingSave() {
		if (this._pendingSaveResolve) {
			this._pendingSaveResolve()
		}
	}

	blockBlur(noteUid: string, blockUid: string) {
		this.socket.emit('block-blur', {
			noteUID: noteUid,
			blockUID: blockUid,
		})
	}

	blockEdit(noteUid: string, blockUid: string) {
		this.socket.emit('block-edit-start', {
			noteUID: noteUid,
			blockUID: blockUid,
		})
	}

	blockEditEnd(noteUid: string, blockUid: string) {
		this.socket.emit('block-edit-end', {
			noteUID: noteUid,
			blockUID: blockUid,
		})
	}

	blockStatusUpdateOn() {
		this.socket.off('block-status-update')
		this.socket.on('block-status-update', (data: any) => {
			if (data.blockUID.includes('temp')) return

			if (data.status.editing) {
				const currentUserId = useCommonStore().userInfo?.info?.userId || localStorage.getItem('userId') || ''
				const editingUserId = data.status.editing.userId || data.status.editing.uid || ''

				// userId가 모두 존재하고 같으면 자기 자신의 편집 → 잠그지 않음
				if (currentUserId && editingUserId && String(currentUserId) === String(editingUserId)) {
					return
				}

				// userId 비교가 불가능한 경우에만 포커스 기반 fallback 사용
				if (!currentUserId || !editingUserId) {
					const { selector } = storeToRefs(useEditorStore())
					if (selector.value && deletePrefix(selector.value.id) === data.blockUID) {
						const targetBlock = document.querySelector(`.editor--block#prefix-${data.blockUID}`)
						const activeEl = document.activeElement
						const isBlockFocused =
							targetBlock && (activeEl === targetBlock || targetBlock.contains(activeEl) || (activeEl?.id === 'editor' && (activeEl as HTMLElement).contains(targetBlock)))
						if (isBlockFocused) {
							return
						}
					}
				}
			}

			const editorBlockList: any = document.querySelector(`.editor--block#prefix-${data.blockUID}`)
			if (!editorBlockList) return
			const container = editorBlockList.closest('.editor--block__container')
			if (!container) return

			const avatarContainer = container.querySelector('.avatar--container')
			const editorBlock = container.querySelector('.editor--block')
			const isTable = editorBlock?.getAttribute('data-type') === 'defaultTable'

			if (data.status.editing) {
				if (!avatarContainer) {
					const newDiv = document.createElement('div')
					newDiv.classList.add('avatar--container')
					newDiv.innerHTML = `<img src="${data.status.editing.avatar}" alt="avatar" class="editor--block__avatar" />`
					container.appendChild(newDiv)
					if (isTable) {
						const cellDivs = editorBlock?.querySelectorAll('td div[contenteditable="true"]') ?? []
						cellDivs.forEach((div: Element) => {
							div.setAttribute('contenteditable', 'false')
							div.setAttribute('data-avatar-locked', 'true')
						})
					} else {
						editorBlock?.setAttribute('contenteditable', 'false')
						editorBlock?.setAttribute('data-avatar-locked', 'true')
					}
				} else {
					const img = avatarContainer.querySelector('img')
					if (img) {
						img.setAttribute('src', data.status.editing.avatar)
					}
				}
			} else if (avatarContainer) {
				avatarContainer.remove()
				if (isTable) {
					const lockedDivs = editorBlock?.querySelectorAll('td div[data-avatar-locked]') ?? []
					lockedDivs.forEach((div: Element) => {
						const parentTd = div.closest('td')
						const cellType = parentTd?.dataset?.type
						if (cellType !== 'error' && cellType !== 'function') {
							div.setAttribute('contenteditable', 'true')
						}
						div.removeAttribute('data-avatar-locked')
					})
				} else {
					editorBlock?.setAttribute('contenteditable', 'true')
					editorBlock?.removeAttribute('data-avatar-locked')
				}
			}
		})
	}

	blockFailedOn() {}

	blockMove(noteUid: string, blockUid: string, prevBlockUid: string) {
		this.socket.off('move-block')
		const data = {
			noteUID: noteUid,
			blockUID: blockUid,
			prevBlockUID: prevBlockUid,
		}
		this.socket.emit('move-block', data)
	}

	blockMoveOn() {
		this.socket.off('retrieve-moved-block')
		this.socket.on('retrieve-moved-block', (data: any) => {
			const { blockUID, prevBlockUID } = data
			const editorBlockList: any = document.querySelector(`.editor--block#prefix-${blockUID}`)
			if (!editorBlockList) return
			const container = editorBlockList.closest('.editor--block__container')
			if (!container) return
			const prevBlock = document.querySelector(`.editor--block#prefix-${prevBlockUID}`)
			if (!prevBlock) return
			const newDivContainer = document.createElement('div')
			newDivContainer.classList.add('editor--block__container')
			const newDivBlock = document.createElement('div')
			newDivBlock.id = `prefix-${blockUID}`
			newDivBlock.classList.add('editor--block')
			newDivBlock.dataset.type = editorBlockList.dataset.type
			newDivBlock.setAttribute('contenteditable', editorBlockList.getAttribute('contenteditable') || 'true')
			newDivBlock.innerHTML = editorBlockList.innerHTML
			newDivContainer.innerHTML = newDivBlock.outerHTML

			editorBlockList.closest('.editor--block__container')?.remove()
			prevBlock.closest('.editor--block__container')?.insertAdjacentElement('afterend', newDivContainer)

			updateBaselineForRemoteBlockMove(blockUID, prevBlockUID)
		})
	}
}

export const socketIO = new SocketIO()
